import { useParams } from "react-router";
import Redirect from "../../components/Redirect";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import FormInput from "../../components/FormInput";
import { motion } from "motion/react";
import Button from "../../components/Button";
import Logo from "../../components/Logo";
import { useMutation } from "@apollo/client";
import { ResetPasswordDocument } from "../../__generated__/graphql";
import { toast } from "react-hot-toast";
import useNav from "../../hooks/useNavigation";

const resetPasswordSchema = z
  .object({
    password: z.string().min(1, "Mot de passe requis"),
    confirmPassword: z.string().min(1, "Mot de passe requis"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Les mots de passe ne correspondent pas",
  });

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const { passwordResetId } = useParams();
  const navigateWithLog = useNav();

  const [resetPassword, { loading: isLoading }] = useMutation(
    ResetPasswordDocument
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  if (!passwordResetId) {
    return <Redirect to="/" />;
  }

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      await resetPassword({
        variables: {
          resetPasswordInput: { password: data.password, passwordResetId },
        },
      });
      toast.success("Mot de passe réinitialisé avec succès");
      setTimeout(() => {
        navigateWithLog("/signin");
      }, 3000);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    }
  };

  return (
    <div className="flex flex-col justify-center items-center h-screen gap-4 bg-background">
      <Logo />
      <motion.div
        className="w-full max-w-md bg-dark border border-border rounded-lg px-4 py-8 text-primary"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-xl font-bold mb-6 text-center">
          Réinitialiser votre mot de passe
        </h1>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormInput
            id="password"
            label="Mot de passe"
            type="password"
            register={register}
            error={errors.password}
          />
          <FormInput
            id="confirmPassword"
            label="Confirmer le mot de passe"
            type="password"
            register={register}
            error={errors.confirmPassword}
          />
          <Button
            type="submit"
            className="w-full h-12 mt-8"
            isLoading={isLoading}
          >
            Réinitialiser
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
